import { useCallback, useEffect, useRef, useState } from "react";
import Log from "./logger.js";

export const useWebSocket = ({
  url,
  event,
  onEvent,
  onError,
  phone,
  reconnectDelay = 3000,
  pingInterval = 30000,
}) => {
  const socketRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  const pingRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const connect = useCallback(() => {
    if (!url || !phone) {
      Log.e("WebSocket URL or phone not provided.");
      return;
    }

    const wsUrl = new URL(url);
    wsUrl.searchParams.set("phone", phone);

    const socket = new WebSocket(wsUrl.toString());
    socketRef.current = socket;

    socket.onopen = () => {
      Log.d("WebSocket connected:", wsUrl.toString());
      setIsConnected(true);

      // Start ping interval
      pingRef.current = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, pingInterval);
    };

    socket.onmessage = (message) => {
      try {
        const parsed =
          typeof message.data === "string" && message.data.startsWith("{")
            ? JSON.parse(message.data)
            : message.data;

        if (!event || parsed?.event === event) {
          onEventRef.current?.(parsed?.data ?? parsed);
        }
      } catch (err) {
        Log.e("WebSocket message parse error:", err);
        onErrorRef.current?.(err);
      }
    };

    socket.onerror = (error) => {
      Log.e("WebSocket error:", error);
      onErrorRef.current?.(error);
    };

    socket.onclose = (evt) => {
      Log.w(
        "WebSocket closed",
        "code:", evt.code,
        "reason:", evt.reason,
        "wasClean:", evt.wasClean
      );
      setIsConnected(false);
      clearInterval(pingRef.current);
      pingRef.current = null;

      // Auto-reconnect
      reconnectTimeoutRef.current = setTimeout(connect, reconnectDelay);
    };
  }, [url, phone, event, reconnectDelay, pingInterval]);

  useEffect(() => {
    connect();

    return () => {
      clearInterval(pingRef.current);
      clearTimeout(reconnectTimeoutRef.current);
      socketRef.current?.close();
    };
  }, [connect]);

  const sendMessage = useCallback((eventName, data) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ event: eventName, data }));
    } else {
      Log.w("WebSocket not connected.");
    }
  }, []);

  return {
    sendMessage,
    socket: socketRef.current,
    isConnected,
  };
};
